import type { JSX, ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import type { SmartCard, SmartCardGridComponent } from "../../types";
import { RuntimeImage, RuntimeLink } from "../../runtime/RendererRuntime";
import { resolveImageValue } from "../../utils/imageOptions";

/**
 * Property-adaptive card grid: each card decides its own layout from whichever
 * properties are filled, so one widget covers media cards, text cards and
 * compact link tiles without per-variant configuration.
 *
 *  - `image`   → media card (16/10 cover image on top, body below)
 *  - no image  → text card with a brand-blue left accent border
 *  - `href`    → the whole card is the link (hover ring + arrow footer)
 *  - `tag`     → small pill above the title
 *  - title only (+ optional href) → compact tile
 *  - a card with no title, no description and no image is skipped
 *  - zero renderable cards → the component renders nothing
 *
 * Column classes are fully enumerated static strings (JIT contract).
 */

const COLUMN_CLASSES: Record<2 | 3 | 4, string> = {
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
};

/** Normalises the authored column count (3 on anything invalid). The admin's
 *  select control stores the value as a string — both forms are accepted. */
function resolveColumns(columns: unknown): 2 | 3 | 4 {
  const n = typeof columns === "string" ? Number(columns) : columns;
  return n === 2 || n === 4 ? n : 3;
}

/** A card is renderable when it has something visible to say. */
function isRenderable(card: SmartCard): boolean {
  const { src } = resolveImageValue(card.image);
  return Boolean(card.title?.trim() || card.description?.trim() || src?.trim());
}

function CardBody({ card, linked }: { card: SmartCard; linked: boolean }) {
  const title = card.title?.trim();
  const description = card.description?.trim();
  const tag = card.tag?.trim();
  const compact = Boolean(title) && !description;
  return (
    <div className={`flex h-full flex-col ${compact ? "justify-center py-4 px-5" : "p-5 md:p-6"}`}>
      {tag ? (
        <span className="mb-2 self-start rounded-full bg-[#0095DB]/10 px-3 py-0.5 text-xs font-semibold text-[#005CA9]">
          {tag}
        </span>
      ) : null}
      {title ? (
        <h4
          className={`font-semibold text-[#005CA9] ${compact ? "text-base md:text-lg" : "text-lg md:text-xl"} ${
            linked ? "group-hover:underline" : ""
          }`}
          style={{ fontFamily: "var(--font-aleo)" }}
        >
          {title}
        </h4>
      ) : null}
      {description ? (
        <p className={`text-base leading-relaxed text-[#0F2849]/80 ${title ? "mt-2" : ""}`}>{description}</p>
      ) : null}
      {linked && !compact ? (
        <span className="mt-auto inline-flex items-center gap-2 pt-4 text-sm font-semibold text-[#005CA9]">
          {card.linkLabel?.trim() || "En savoir plus"}
          <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
        </span>
      ) : null}
      {linked && compact ? (
        <span className="mt-1 inline-flex items-center gap-2 text-sm font-semibold text-[#005CA9]">
          <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
        </span>
      ) : null}
    </div>
  );
}

function renderCard(card: SmartCard, index: number): JSX.Element | null {
  if (!isRenderable(card)) return null;
  const { src: imageSrc } = resolveImageValue(card.image);
  const hasImage = Boolean(imageSrc?.trim());
  const linked = Boolean(card.href);

  const surface = hasImage
    ? "overflow-hidden rounded-xl bg-white ring-1 ring-black/5 shadow-sm"
    : "rounded-xl border-l-[3px] border-l-[#005CA9] bg-white ring-1 ring-black/5 shadow-sm";
  const hover = linked ? "transition hover:ring-[#0095DB]/40 hover:shadow-md" : "";
  const className = `group flex h-full flex-col ${surface} ${hover}`;

  const inner: ReactNode = (
    <>
      {hasImage ? (
        <div className="aspect-[16/10] w-full overflow-hidden">
          <RuntimeImage
            src={imageSrc as string}
            alt={card.imageAlt ?? ""}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
          />
        </div>
      ) : null}
      <CardBody card={card} linked={linked} />
    </>
  );

  if (linked) {
    return (
      <RuntimeLink key={index} href={card.href} className={className} data-smart-card={index}>
        {inner}
      </RuntimeLink>
    );
  }
  return (
    <div key={index} className={className} data-smart-card={index}>
      {inner}
    </div>
  );
}

/** Renderer for the property-adaptive card grid. */
export function SmartCardGridRenderer({ component }: { component: SmartCardGridComponent }) {
  const content = component.content ?? ({} as SmartCardGridComponent["content"]);
  const cards = Array.isArray(content.cards) ? content.cards : [];
  const rendered = cards
    .map((card, index) => renderCard(card ?? {}, index))
    .filter((node): node is JSX.Element => node !== null);
  if (rendered.length === 0) return null;

  const columnsClass = COLUMN_CLASSES[resolveColumns(content.columns)];
  const title = content.title?.trim();
  return (
    <section data-smart-card-grid>
      {title ? (
        <h3
          className="mb-4 text-xl md:text-2xl font-semibold text-[#005CA9]"
          style={{ fontFamily: "var(--font-aleo)" }}
        >
          {title}
        </h3>
      ) : null}
      <div className={`grid gap-4 md:gap-6 ${columnsClass}`}>{rendered}</div>
    </section>
  );
}
